import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap, useMapEvents, Marker, Popup } from 'react-leaflet';
import L, { geoJSON } from 'leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { fetchTierData, fetchHierarchyData } from 'store/locationSlice';
import { toast } from 'react-toastify';
import CustomToast from 'components/toast/customToast';
import { BASE_URL } from '../../../commonConfig/ApiConfig';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import { addMapIndicator } from 'store/workspaceSlice';
import { BiSearch } from 'react-icons/bi';
import { FaChevronUp, FaChevronDown, FaChevronLeft, FaChevronRight, FaHome, FaCircle, FaPlus, FaMinus } from 'react-icons/fa';

// Add inline styles for map labels
const mapLabelStyles = `
  .tier-label-icon {
    pointer-events: none;
    transition: opacity 0.3s ease-in-out;
  }

  .tier-label-icon div {
    font-size: 13px;
    font-weight: 600;
    color: #1f3c88;
    text-shadow: 
      -1px -1px 3px #fff,
      1px -1px 3px #fff,
      -1px 1px 3px #fff,
      1px 1px 3px #fff,
      0 0 5px #fff,
      0 0 8px #fff;
    white-space: nowrap;
    text-align: center;
    line-height: 1.2;
    background: transparent;
  }

  .district-label-icon div {
    color: #161b16ff;
    font-size: 11px;
    font-weight: 600;
    text-shadow: 
      -1px -1px 3px #fff,
      1px -1px 3px #fff,
      -1px 1px 3px #fff,
      1px 1px 3px #fff,
      0 0 5px #fff,
      0 0 8px #fff;
  }
`;

// Inject styles into document
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.innerHTML = mapLabelStyles;
  document.head.appendChild(styleElement);
}
// Custom Zoom Control Component
const ZoomControl = () => {
  const map = useMap();

  const zoomIn = () => {
    map.zoomIn();
  };

  const zoomOut = () => {
    map.zoomOut();
  };

  return (
    <div className="position-absolute" style={{ top: '10px', left: '10px', zIndex: 1000 }}>
      <div
        className="bg-white border rounded shadow-sm"
        style={{
          width: '40px',
          height: '80px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '5px'
        }}
      >
        <button
          className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
          onClick={zoomIn}
          title="Zoom In"
          style={{
            width: '35px',
            height: '35px',
            backgroundColor: 'white',
            border: '1px solid #748194',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '5px',
            padding: '2px',
            // boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <FaPlus size={16} color="#6c757d" />
        </button>
        <button
          className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
          onClick={zoomOut}
          title="Zoom Out"
          style={{
            width: '35px',
            height: '35px',
            backgroundColor: 'white',
            border: '1px solid #748194',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2px',
            // boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <FaMinus size={16} color="#6c757d" />
        </button>
      </div>
    </div>
  );
};

const PanControl = ({ selectedFeature }) => {
  const map = useMap();
  const panDistance = 0.5; // degrees

  // Dynamically reset map to selected feature using its bounds
  const resetToSelectedFeature = () => {
    if (selectedFeature && selectedFeature.geojson) {
      let geojsonLayer = geoJSON(selectedFeature.geojson);
      map.fitBounds(geojsonLayer.getBounds()); // sets center & zoom dynamically
    }
  };

  // Auto-center map whenever the selected feature changes
  useEffect(() => {
    resetToSelectedFeature();
  }, [selectedFeature]);

  const panNorth = () => {
    const currentCenter = map.getCenter();
    map.setView([currentCenter.lat + panDistance, currentCenter.lng], map.getZoom());
  };

  const panSouth = () => {
    const currentCenter = map.getCenter();
    map.setView([currentCenter.lat - panDistance, currentCenter.lng], map.getZoom());
  };

  const panEast = () => {
    const currentCenter = map.getCenter();
    map.setView([currentCenter.lat, currentCenter.lng + panDistance], map.getZoom());
  };

  const panWest = () => {
    const currentCenter = map.getCenter();
    map.setView([currentCenter.lat, currentCenter.lng - panDistance], map.getZoom());
  };

  const zoomIn = () => map.zoomIn();
  const zoomOut = () => map.zoomOut();

  return (
    <div className="position-absolute" style={{ top: '10px', right: '10px', zIndex: 1000 }}>
      <div
        className="bg-white border rounded-circle shadow-sm"
        style={{
          width: '120px',
          height: '120px',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Home Button - Center */}
        <button
          className="btn btn-sm btn-outline-primary rounded-circle"
          onClick={resetToSelectedFeature}
          title="Reset to selected feature"
          style={{
            width: '35px',
            height: '35px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-52%, -52%)',
            zIndex: 2
          }}
        >
          <FaCircle size={14} style={{ transform: 'translate(-2px, -1px)' }} />
        </button>

        {/* North Button */}
        <button
          className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
          onClick={panNorth}
          title="Pan North"
          style={{
            position: 'absolute',
            top: '5px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '35px',
            height: '35px',
          }}
        >
          <FaChevronUp size={45} />
        </button>

        {/* East Button */}
        <button
          className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
          onClick={panEast}
          title="Pan East"
          style={{
            position: 'absolute',
            right: '5px',
            top: '50%',
            transform: 'translateY(-51%)',
            width: '35px',
            height: '35px',
          }}
        >
          <FaChevronRight size={40} />
        </button>

        {/* South Button */}
        <button
          className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
          onClick={panSouth}
          title="Pan South"
          style={{
            position: 'absolute',
            bottom: '5px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '35px',
            height: '35px',
          }}
        >
          <FaChevronDown size={45} />
        </button>

        {/* West Button */}
        <button
          className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
          onClick={panWest}
          title="Pan West"
          style={{
            position: 'absolute',
            left: '5px',
            top: '50%',
            transform: 'translateY(-52%)',
            width: '35px',
            height: '35px',
          }}
        >
          <FaChevronLeft size={40} />
        </button>
      </div>
    </div>
  );
};

const GeoJsonHighlighter = ({ geoJsonData, onFeatureSelect, selectedFeature,showSelectBtn }) => {
  const dispatch = useDispatch();
  const location = useLocation();


  const map = useMap();

  useEffect(() => {
    if (geoJsonData) {
      const geoJsonLayer = L.geoJSON(geoJsonData);
      map.flyToBounds(geoJsonLayer.getBounds(), { padding: [20, 20] });
    }
  }, [geoJsonData, map]);

  const geoJsonStyle = {
    color: '#68a2ff',
    weight: 2,
    opacity: 1,
    fillOpacity: 1,
    fillColor: '#f3efe9',
  };

  const onEachFeature = (feature, layer) => {
    const name = feature.properties.name || 'Unnamed Feature';
    const showSelectButton = showSelectBtn !== "ulb";

  //   const popupContent = `
  //   <div style="padding: 15px 15px 7.5px 15px; text-align: center;">
  //   <strong> ${name}</strong>
  //     <br><br>
  //     <button class="btn btn-primary btn-sm select-btn">Select</button>
  //     <!-- <button class="btn btn-primary btn-sm workspace-btn">Add to Workspace</button> -->
  //   </div>
  // `;
    const popupContent = `
    <div style="padding: 15px 15px 7.5px 15px; text-align: center;">
      <strong>${name}</strong>
      <br><br>
      ${showSelectButton ? `<button class="btn btn-primary btn-sm select-btn">Select</button>` : ''}
      <!-- <button class="btn btn-primary btn-sm workspace-btn">Add to Workspace</button> -->
    </div>
  `;
    layer.bindPopup(popupContent);
    layer.on('popupopen', (e) => {
      const popupEl = e.popup.getElement();

      const selectBtn = popupEl.querySelector('.select-btn');
      const workspaceBtn = popupEl.querySelector('.workspace-btn');

      if (selectBtn) {
        selectBtn.addEventListener('click', () => {
          if (onFeatureSelect) onFeatureSelect(feature)
        });
      }

      /* if (workspaceBtn) {
        workspaceBtn.addEventListener('click', () => {
          const locationName = feature.properties.name || feature.properties.code || "Location";
          toast(
            <CustomToast message={`${locationName} added to workspace`} type="loaction" />,
            { position: "top-right" }
          );
          dispatch(addMapIndicator({
            id: feature.properties.code || feature.properties.name,
            geo_level_code: feature.properties.geo_level_code,
            name: feature.properties.name,
            geometry: feature.geometry,
            properties: feature.properties,
            sourceType: 'mapPopup',
            addedAt: new Date().toISOString()
          }));

        });
      } */
    });
  };
  return geoJsonData ? (
    <GeoJSON data={geoJsonData} style={geoJsonStyle} onEachFeature={onEachFeature} selectedFeature={selectedFeature} />
  ) : null;
};

const SimpleMap = ({ geoJsonData, onFeatureSelect, selectedFeature , level}) => {
  const dispatch = useDispatch();
  const tierData = useSelector((state) => state.locationTire.tierLocations || {});
  const dataToShow = geoJsonData;
  const [showSelectBtn, setShowSelectBtn] = useState(level);

  // Search related states
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Tier dropdown states
  const [showTierDropdown, setShowTierDropdown] = useState(false);

  // ULG dropdown states
  const [showUlgDropdown, setShowUlgDropdown] = useState(false);
  const [selectedUlgTypes, setSelectedUlgTypes] = useState(new Set());
  const [ulgData, setUlgData] = useState({}); // Store ULG data by type


  // Existing states
  const [visibleTiers, setVisibleTiers] = useState(new Set());
  const location = useLocation();

  // Search function
  const handleSearch = async () => {
    if (!query || query.trim().length === 0) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const payload = {
        lgd_code: selectedFeature?.lgd_code || 1000,
        geo_level: selectedFeature?.geo_level_code || 1,
        search_text: query.trim()
      };

      const response = await axios.post(`${BASE_URL}locations/text-search`, payload);

      if (response.data && response.data.locations) {
        setSearchResults(response.data.locations);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(true);
      }
    } catch (error) {
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search result click
  const handleResultClick = (searchResult) => {
    // Create a feature object similar to map click
    const feature = {
      properties: {
        code: searchResult.lgdcode,
        name: searchResult.label,
        geo_level_code: searchResult.geo_level_code
      },
      geo_level: searchResult.geo_level_code,
      code: searchResult.lgdcode,
      name: searchResult.label
    };

    // Close search results
    setShowResults(false);
    setQuery('');

    // Trigger the same navigation as map click
    if (onFeatureSelect) {
      onFeatureSelect(feature);
    }
  };

  // Search on enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchIconClick();
    }
  };

  // Handle search icon click - selects first result or shows results
  const handleSearchIconClick = async () => {
    if (query.trim().length < 3) {
      return;
    }

    // If results are already shown and there are results, select the first one
    if (showResults && searchResults.length > 0) {
      handleResultClick(searchResults[0]);
      return;
    }

    // Otherwise, perform search and show results
    await handleSearch();
  };

  // Close search results when clicking outside
  const handleClickOutside = () => {
    setShowResults(false);
  };

  // Debounced search - Updated to trigger only after 4 characters
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim().length >= 4) {
        handleSearch();
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleTierClick = (tier) => {
    if (visibleTiers.has(tier)) {
      // If tier is visible, hide it by removing from the set
      const newVisibleTiers = new Set(visibleTiers);
      newVisibleTiers.delete(tier);
      setVisibleTiers(newVisibleTiers);
    } else {
      // If tier is not visible, show it by adding to the set and fetch data
      const newVisibleTiers = new Set(visibleTiers);
      newVisibleTiers.add(tier);
      setVisibleTiers(newVisibleTiers);
      dispatch(fetchTierData({
        geo_level: selectedFeature.geo_level_code,
        parent_lgd_code: selectedFeature.lgd_code === 1000 ? null : selectedFeature.lgd_code,
        searchType: tier
      }));
    }
  };

  // Toggle tier dropdown
  const toggleTierDropdown = () => {
    setShowTierDropdown(!showTierDropdown);
  };

  // Close tier dropdown when clicking outside
  const handleTierDropdownClickOutside = () => {
    setShowTierDropdown(false);
  };

  /**
   * Toggle ULG dropdown visibility
   * Purpose: Shows/hides the ULG type selection dropdown and closes tier dropdown if open
   * Functionality: Toggles the ULG dropdown state and ensures only one dropdown is open at a time
   */
  const toggleUlgDropdown = () => {
    setShowUlgDropdown(!showUlgDropdown);
    setShowTierDropdown(false); // Close tier dropdown if open
  };


  const handleUlgDropdownClickOutside = () => {
    setShowUlgDropdown(false);
  };


  const handleUlgTypeClick = async (ulgType) => {
    const newSelectedUlgTypes = new Set(selectedUlgTypes);

    if (newSelectedUlgTypes.has(ulgType.name)) {
      // If ULG type is selected, deselect it
      newSelectedUlgTypes.delete(ulgType.name);
      // Remove ULG data for this type
      const newUlgData = { ...ulgData };
      delete newUlgData[ulgType.name];
      setUlgData(newUlgData);
    } else {
      // If ULG type is not selected, select it and fetch data
      newSelectedUlgTypes.add(ulgType.name);

      // Fetch ULG data using the new API endpoint
      try {
        const payload = {
          geo_level: selectedFeature.geo_level_code,
          lgd_code: selectedFeature.lgd_code,
          ulg_type_code: ulgType.id
        };


        const response = await axios.post(`${BASE_URL}locations/search-ulg`, payload);

        if (response.data && response.data.locations) {
          // Store the ULG data for this type
          setUlgData(prev => ({
            ...prev,
            [ulgType.name]: response.data.locations
          }));

        } else {
        }
      } catch (error) {
        toast(
          <CustomToast message={`Error fetching ${ulgType.name} data`} type="error" />,
          { position: "top-right" }
        );
      }
    }
    setSelectedUlgTypes(newSelectedUlgTypes);
  };


  const ulgTypes = [
    { id: 1, name: 'City Corporation', color: '#FF0000' }, // Red
    { id: 2, name: 'City Municipal Council', color: '#FF8C00' }, // Dark Orange
    { id: 3, name: 'Municipal Board', color: '#FFD700' }, // Gold
    { id: 4, name: 'Municipal Committee', color: '#FFFF00' }, // Yellow

    { id: 5, name: 'Municipal Corporation', color: '#00FF00' }, // Lime Green
    { id: 6, name: 'Municipal Council', color: '#32CD32' }, // Forest Green
    { id: 7, name: 'Municipality', color: '#00CED1' }, // Dark Turquoise
    { id: 8, name: 'Municipality(Nagar Palika)', color: '#00FFFF' }, // Cyan

    { id: 9, name: 'Nagar Palika', color: '#0000FF' }, // Blue
    { id: 10, name: 'Nagar Panchayat', color: '#4169E1' }, // Royal Blue
    { id: 11, name: 'Nct Municipal Council', color: '#87CEEB' }, // Sky Blue
    { id: 12, name: 'Notified Area', color: '#000080' }, // Navy

    { id: 13, name: 'Notified Area Committee', color: '#4B0082' }, // Indigo
    { id: 14, name: 'Notified Area Council', color: '#8A2BE2' }, // Blue Violet
    { id: 15, name: 'Town Committee', color: '#9400D3' }, // Dark Violet
    { id: 16, name: 'Town Council', color: '#FF00FF' }, // Magenta
    { id: 17, name: 'Town Municipal Council', color: '#DC143C' }, // Crimson
    { id: 18, name: 'Town Panchayat', color: '#FF69B4' }  // Hot Pink
  ];


  /**
   * Create colored markers for ULG types
   * Purpose: Generates custom Leaflet markers with different colors for different ULG types
   * Functionality:
   * - Creates a div icon with a base ULG icon image
   * - Applies different colors for each ULG type
   * - Returns a Leaflet DivIcon with proper sizing and positioning
   * - Each ULG type gets a unique colored marker for easy identification on the map
   * @param {string} color - Hex color code for the ULG type (e.g., '#000080')
   * @returns {L.DivIcon} - Leaflet DivIcon object for map rendering
   */
  const createUlgIcon = (color) => {
    const svgIcon = `
      <div style="
        width: 15px; 
        height: 15px; 
        background-image: url('https://cdn-icons-png.flaticon.com/128/149/149059.png');
        background-size: contain;
        background-repeat: no-repeat;
        filter: hue-rotate(${getHueRotation(color)});
      "></div>
    `;

    return new L.DivIcon({
      html: svgIcon,
      className: 'custom-ulg-icon',
      iconSize: [15, 15],
      iconAnchor: [14, 28],
      popupAnchor: [0, -28],
    });
  };


  const getHueRotation = (hexColor) => {
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);

    // Convert RGB to HSL and return hue rotation
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const diff = max - min;

    let hue = 0;
    if (diff !== 0) {
      if (max === r) hue = ((g - b) / diff) % 6;
      else if (max === g) hue = (b - r) / diff + 2;
      else hue = (r - g) / diff + 4;
    }
    hue = Math.round(hue * 60);
    if (hue < 0) hue += 360;

    return `${hue}deg`;
  };




  // Fetch data for all tiers when they're first added to visibleTiers
  useEffect(() => {
    // Make sure we have a selected feature before fetching
    if (selectedFeature && visibleTiers.size > 0) {
      // For each visible tier, ensure we have data
      visibleTiers.forEach(tier => {
        dispatch(fetchTierData({
          geo_level: selectedFeature.geo_level_code,
          parent_lgd_code: selectedFeature.lgd_code === 1000 ? null : selectedFeature.lgd_code,
          searchType: tier
        }));
      });
    }
  }, [selectedFeature, dispatch, visibleTiers]);

  // Automatically trigger All ULB when geo_level_code is 2
  useEffect(() => {
    if (selectedFeature?.geo_level_code === 3) {
      // Add "All ULB" to visibleTiers
      setVisibleTiers(prev => {
        const newVisibleTiers = new Set(prev);
        newVisibleTiers.add("All ULB");
        return newVisibleTiers;
      });

      // Fetch data for "All ULB"
      dispatch(fetchTierData({
        geo_level: selectedFeature.geo_level_code,
        parent_lgd_code: selectedFeature.lgd_code === 1000 ? null : selectedFeature.lgd_code,
        searchType: "All ULB"
      }));
    } else {
      // Remove All ULB if another feature is selected
      setVisibleTiers(prev => {
        const newTiers = new Set(prev);
        newTiers.delete("All ULB");
        return newTiers;
      });
    }
  }, [selectedFeature, dispatch]);

  const visibleLocations = React.useMemo(() => {
    const allLocations = [];

    // Add tier locations
    for (let tier of visibleTiers) {
      const tierLocs = tierData[tier] || [];
      allLocations.push(...tierLocs.map(loc => ({
        ...loc,
        tierType: tier,
        displayAs: tier // Default display type
      })));
    }


    return allLocations;
  }, [tierData, visibleTiers]);

  const tier1Icon = new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/128/684/684908.png", // Replace with Tier 1 icon URL
    iconSize: [20, 20],
    iconAnchor: [10, 20],
    popupAnchor: [0, -20],
  });

  const tier2Icon = new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/128/18560/18560647.png", // Replace with Tier 2 icon URL
    iconSize: [20, 20],
    iconAnchor: [10, 20],
    popupAnchor: [0, -20],
  });
  const tier3Icon = L.divIcon({
    html: `<img src="https://cdn-icons-png.flaticon.com/128/684/684908.png" 
           style="width: 20px; height: 20px; filter: brightness(0) saturate(100%) invert(58%) sepia(93%) saturate(419%) hue-rotate(81deg) brightness(91%) contrast(92%);" />`,
    className: 'custom-tier-icon',
    iconSize: [20, 20],
    iconAnchor: [10, 20],
    popupAnchor: [0, -20],
  });
  const ulbIcon = new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/128/7945/7945007.png", // Icon for All ULB
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });

  if (!dataToShow) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100%', width: '100%' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  const isLocationPage = location.pathname.includes("/location");
  return (
    <>

      <MapContainer
        center={[20.5937, 78.9629]}
        zoom={5}
        minZoom={3}
        maxZoom={18}
        maxBounds={[[6.75, 68.0], [40.07, 97.5]]}
        dragging={false}          // Disable panning via drag
        scrollWheelZoom={false}   // Disable zoom via mouse wheel
        doubleClickZoom={false}   // Disable zoom via double click
        boxZoom={false}           // Disable zoom via box selection
        zoomControl={false}       // Disable default zoom control buttons
        style={{ height: '90%', width: '100%' }}
      >
        {/*{isLocationPage && (
        <div className="position-absolute top-0 pt-3 w-100 fs-1" style={{ backgroundColor: '#f8f9fa', padding: '8px 8px', zIndex: 1000 }}>
          <p className="text-center small mb-0" style={{ color: '#6c757d', margin: '0' }}>
             <i className="fas fa-mouse-pointer me-1"></i> 
            Click on the map to explore States / UTs, Districts, and ULGs.
          </p>
        </div>)}
      <div className='position-relative'></div>*/}
      {(showSelectBtn!== 'ulb'&& showSelectBtn!== 'district'&& showSelectBtn!== 'ulg' && showSelectBtn!== 'state') && (
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" /> 
      )}
        {/* Custom Zoom Controls - Top Left */}
        <ZoomControl />

        {/* Custom Pan Controls */}
        <PanControl selectedFeature={selectedFeature} />

        {/* Search Bar - Updated with search icon and improved styling */}
        {(location.pathname === '/location' || location.pathname === '/') && (
          <div className="position-absolute top-1 pt-2" style={{ zIndex: 1000, width: '55%', left: '10%' }}>
            <div className="w-100">

              {/* Input Group with Icon */}
              <div className="input-group mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search locations..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <span className="input-group-text" style={{ cursor: 'pointer' }} onClick={handleSearchIconClick}>
                  <BiSearch size={18} className="" />
                </span>
              </div>

              {/* Search Results Dropdown */}
              {showResults && (
                <div
                  className="position-relative bg-white border rounded shadow-sm w-100 mt-n2"
                  style={{ maxHeight: '300px', overflowY: 'auto', zIndex: 1001 }}
                >
                  {isSearching ? (
                    <div className="p-3 text-center">
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Searching...</span>
                      </div>
                    </div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((result, index) => (
                      <div
                        key={index}
                        className="p-2 border-bottom bg-white"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleResultClick(result)}
                        onMouseEnter={(e) => e.currentTarget.classList.add('bg-light')}
                        onMouseLeave={(e) => e.currentTarget.classList.remove('bg-light')}
                      >
                        <div className="fw-bold text-dark">{result.label}</div>
                        <small className="text-muted">
                          {result.geo_level}
                          {result.hierarchy?.length > 0 && ` | ${result.hierarchy.join(', ')}`}
                        </small>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-muted text-center">
                      No locations found for "{query}"
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <GeoJsonHighlighter geoJsonData={dataToShow} onFeatureSelect={onFeatureSelect} selectedFeature={selectedFeature} showSelectBtn={showSelectBtn} />
          <ZoomLabelHandler
  selectedFeature={selectedFeature}
  dispatch={dispatch}
/>

        {selectedFeature?.geo_level?.toLowerCase() === "ulg" &&
          selectedFeature?.latitude && selectedFeature?.longitude &&
          !isNaN(parseFloat(selectedFeature.latitude)) && !isNaN(parseFloat(selectedFeature.longitude)) && (
            <Marker
              key={selectedFeature.lgd_code}
              position={[parseFloat(selectedFeature.latitude), parseFloat(selectedFeature.longitude)]}
              icon={ulbIcon}
            >
              <Popup>
                <div className="text-center" style={{ padding: '15px 15px 7.5px 15px' }}>
                  <strong className="d-block mb-3">{selectedFeature.name}</strong>{showSelectBtn!=="ulb" ?
                  (
                    <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      if (onFeatureSelect) {
                        onFeatureSelect(selectedFeature);
                      }
                    }}
                  >
                    Select
                  </button>):("")}
                  {/* Add to Workspace button commented out */}
                </div>
              </Popup>
            </Marker>
          )}

        {visibleLocations?.map((loc, index) => {
          // Validate coordinates before rendering marker
          if (!loc.latitude || !loc.longitude ||
            isNaN(parseFloat(loc.latitude)) || isNaN(parseFloat(loc.longitude))) {
            return null;
          }

          // Determine which icon to use based on display type
          let icon;
          let displayType = (loc.displayAs === 'All ULB' ? 'ULG' : loc.displayAs) || loc.tierType;

          if (loc.geo_level === 4 && loc.tier_level !== "Tier-3" && loc.tier_level !== "Tier-2" && loc.tier_level !== "Tier-1") {
            icon = ulbIcon;
          } else if (loc.tier_level === "Tier-1") {
            icon = tier1Icon;
          } else if (loc.tier_level === "Tier-2") {
            icon = tier2Icon;
          } else if (loc.tier_level === "Tier-3") {
            icon = tier3Icon;
          } else {
            icon = tier2Icon; // Default fallback
          }
          // Create unique key to avoid duplicates
          const uniqueKey = `${loc.code}-${displayType}-${index}`;

          return (
            <Marker key={uniqueKey} icon={icon} position={[parseFloat(loc.latitude), parseFloat(loc.longitude)]}>
              <Popup>
                <div className="text-center" style={{ padding: '15px 15px 7.5px 15px' }}>
                  <strong className="d-block mb-2">{loc.name}</strong>
                  <small className="text-muted d-block mb-3">
                    {displayType}
                  </small>{showSelectBtn!=="ulb" ?
                  (<button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      if (onFeatureSelect) {
                        onFeatureSelect(loc);
                      }
                    }}
                  >
                    Select
                  </button>):("")}
                  {/* Add to Workspace button commented out */}
                </div>
              </Popup>
            </Marker>
          )
        })}

     
        {Object.entries(ulgData).map(([ulgTypeName, locations]) => {
          const ulgTypeInfo = ulgTypes.find(ulg => ulg.name === ulgTypeName);
          if (!ulgTypeInfo || !locations || locations.length === 0) return null;

          return locations.map((location, index) => {
            if (!location.latitude || !location.longitude ||
              isNaN(parseFloat(location.latitude)) || isNaN(parseFloat(location.longitude))) {
              return null;
            }

            const icon = createUlgIcon(ulgTypeInfo.color);
            const uniqueKey = `ulg-${ulgTypeName}-${location.lgd_code || location.id}-${index}`;

            return (
              <Marker
                key={uniqueKey}
                icon={icon}
                position={[parseFloat(location.latitude), parseFloat(location.longitude)]}
              >
                <Popup>
                  <div className="text-center" style={{ padding: '15px 15px 7.5px 15px' }}>
                    <strong className="d-block mb-2">{location.name}</strong>
                    <small className="text-muted d-block mb-3">{ulgTypeName}</small>{showSelectBtn!=="ulb" ?
                  (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        if (onFeatureSelect) {
                          // Transform ULG location to the format MapPage expects
                          const formattedLocation = {
                            properties: {
                              code: location.lgd_code || location.lgdcode || location.code,
                              name: location.name,
                              geo_level_code: location.geo_level_code || 4
                            },
                            geo_level: location.geo_level_code || 4,
                            code: location.lgd_code || location.lgdcode || location.code,
                            name: location.name,
                            lgd_code: location.lgd_code || location.lgdcode || location.code,
                            geo_level_code: location.geo_level_code || 4
                          };
                          onFeatureSelect(formattedLocation);
                        }
                      }}
                    >
                      Select
                    </button>):("")}
                    {/* Add to Workspace button commented out */}
                  </div>
                </Popup>
              </Marker>
            );
          });
        })}

        {/* Tier and ULG Dropdowns - Updated Section */}
        {(location.pathname === '/location' || location.pathname === '/') && (
          <div style={{
            position: 'absolute',
            bottom: '20px',
            left: '10px',
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            zIndex: 1000
          }}>
            {/* Tier Dropdown */}
            <div className="position-relative">
              <button
                className="btn btn-light d-flex align-items-center justify-content-between rounded-pill"
                onClick={toggleTierDropdown}
                style={{ minWidth: '160px' }}
              >
                <span>Tier</span>
                <svg
                  className="ms-2"
                  style={{ width: '12px', height: '12px' }}
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                </svg>
              </button>

              {/* Tier Dropdown Menu */}
              {showTierDropdown && (
                <div
                  className="position-absolute bg-white border rounded shadow-sm"
                  style={{
                    bottom: '100%',
                    left: 0,
                    minWidth: '160px',
                    marginBottom: '8px'
                  }}
                >
                  {/* Tier 1 Option */}
                  <div
                    className="p-2 d-flex align-items-center border-bottom"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: visibleTiers.has('Tier1') ? '#e3f2fd' : 'white'
                    }}
                    onClick={() => handleTierClick("Tier1")}
                    onMouseEnter={(e) => {
                      if (!visibleTiers.has('Tier1')) {
                        e.target.style.backgroundColor = '#f1f3f4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!visibleTiers.has('Tier1')) {
                        e.target.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={visibleTiers.has('Tier1')}
                      readOnly
                      className="me-2"
                    />
                    <img
                      src="https://cdn-icons-png.flaticon.com/128/684/684908.png"
                      alt="Tier 1 Icon"
                      style={{ width: '20px', height: '20px', marginRight: '8px' }}
                    />
                    <span>Tier 1</span>
                  </div>

                  {/* Tier 2 Option */}
                  <div
                    className="p-2 d-flex align-items-center"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: visibleTiers.has('Tier2') ? '#e3f2fd' : 'white'
                    }}
                    onClick={() => handleTierClick("Tier2")}
                    onMouseEnter={(e) => {
                      if (!visibleTiers.has('Tier2')) {
                        e.target.style.backgroundColor = '#f1f3f4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!visibleTiers.has('Tier2')) {
                        e.target.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={visibleTiers.has('Tier2')}
                      readOnly
                      className="me-2"
                    />
                    <img
                      src="https://cdn-icons-png.flaticon.com/128/18560/18560647.png"
                      alt="Tier 2 Icon"
                      style={{ width: '20px', height: '20px', marginRight: '8px' }}
                    />
                    <span>Tier 2</span>
                  </div>
                  {/* ADD THIS TIER 3 OPTION */}
                  <div
                    className="p-2 d-flex align-items-center"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: visibleTiers.has('Tier3') ? '#e3f2fd' : 'white'
                    }}
                    onClick={() => handleTierClick("Tier3")}
                    onMouseEnter={(e) => {
                      if (!visibleTiers.has('Tier3')) {
                        e.target.style.backgroundColor = '#f1f3f4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!visibleTiers.has('Tier3')) {
                        e.target.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={visibleTiers.has('Tier3')}
                      readOnly
                      className="me-2"
                    />
                    <img
                      src="https://cdn-icons-png.flaticon.com/128/684/684908.png"
                      alt="Tier 3 Icon"
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '8px',
                        filter: 'brightness(0) saturate(100%) invert(58%) sepia(93%) saturate(419%) hue-rotate(81deg) brightness(91%) contrast(92%)'
                      }}
                    />
                    <span>Tier 3</span>
                  </div>
                </div>
              )}
            </div>

            <div className="position-relative">
              <button
                className="btn btn-light d-flex align-items-center justify-content-between rounded-pill"
                onClick={toggleUlgDropdown}
                style={{ minWidth: '160px' }}
              >
                <span>Type of ULG</span>
                <svg
                  className="ms-2"
                  style={{ width: '12px', height: '12px' }}
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                </svg>
              </button>


              {showUlgDropdown && (
                <div
                  className="position-absolute bg-white border rounded shadow-sm"
                  style={{
                    bottom: '100%',
                    left: 0,
                    minWidth: '220px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    marginBottom: '8px'
                  }}
                >
                  {ulgTypes.map((ulgType, index) => {
                    const isChecked = selectedUlgTypes.has(ulgType.name);

                    return (
                      <div
                        key={index}
                        className={`p-2 d-flex align-items-center ${index !== ulgTypes.length - 1 ? 'border-bottom' : ''}`}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: isChecked ? '#e3f2fd' : 'white'
                        }}
                        onClick={(e) => {
                          // Only handle click if it's not on the checkbox
                          if (e.target.type !== 'checkbox') {
                            handleUlgTypeClick(ulgType);
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (!isChecked) {
                            e.target.style.backgroundColor = '#f1f3f4';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isChecked) {
                            e.target.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleUlgTypeClick(ulgType)}
                          className="me-2"
                        />
                        <img
                          src="https://cdn-icons-png.flaticon.com/128/149/149059.png"
                          alt="ULG Icon"
                          style={{
                            width: '20px',
                            height: '20px',
                            marginRight: '8px',
                            filter: `hue-rotate(${getHueRotation(ulgType.color)}) saturate(1.5) brightness(1.2)`
                          }}
                        />
                        <span style={{ fontSize: '14px' }}>
                          {ulgType.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        )}

        {/* Click outside handlers */}
        {showResults && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={handleClickOutside}
          />
        )}

        {(showTierDropdown || showUlgDropdown) && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={() => {
              handleTierDropdownClickOutside();
              handleUlgDropdownClickOutside();
            }}
          />
        )}
      </MapContainer>
    </>
  );

};
function ZoomLabelHandler({ selectedFeature, dispatch }) {
  const [zoom, setZoom] = React.useState(5);
  const [visibleLabels, setVisibleLabels] = React.useState({ state: [], district: [] });
  const hierarchyData = useSelector((state) => state.locationTire.hierarchyData || { state: [], district: [] });
  
  const map = useMapEvents({
    zoomend: () => setZoom(map.getZoom()),
    moveend: () => {
      filterVisibleLabels();
    }
  });

  const geoLevel = selectedFeature?.geo_level_code || 1;
  const lgdCode = selectedFeature?.lgd_code || 1000;

  // Fetch hierarchy data when selectedFeature changes
  useEffect(() => {
    if (selectedFeature) {
      dispatch(fetchHierarchyData({
        geo_level: geoLevel,
        lgd_code: lgdCode
      }));
    }
  }, [dispatch, selectedFeature, geoLevel, lgdCode]);

  // Filter visible labels dynamically
  const filterVisibleLabels = React.useCallback(() => {
    if (!map) return;

    const bounds = map.getBounds();
    const mapZoom = map.getZoom();

    const getMinDistance = (zoomLevel) => {
      if (zoomLevel <= 5) return 100; // km for states
      if (zoomLevel <= 7) return 50;  // km for districts
      if (zoomLevel <= 9) return 20;
      return 10;
    };

    const minDistance = getMinDistance(mapZoom);

    const filterByDistance = (locations) => {
      if (!locations || locations.length === 0) return [];
      const visible = [];
      const positions = [];

      const sorted = [...locations].sort((a, b) => {
        const center = map.getCenter();
        const distA = Math.sqrt((a.latitude - center.lat) ** 2 + (a.longitude - center.lng) ** 2);
        const distB = Math.sqrt((b.latitude - center.lat) ** 2 + (b.longitude - center.lng) ** 2);
        return distA - distB;
      });

      for (const loc of sorted) {
        if (!loc.latitude || !loc.longitude) continue;
        const lat = parseFloat(loc.latitude);
        const lng = parseFloat(loc.longitude);
        if (!bounds.contains([lat, lng])) continue;

        let tooClose = false;
        for (const pos of positions) {
          const distance = map.distance([lat, lng], pos) / 1000; // km
          if (distance < minDistance) {
            tooClose = true;
            break;
          }
        }

        if (!tooClose) {
          visible.push(loc);
          positions.push([lat, lng]);
        }
      }

      return visible;
    };

    // ✅ Apply filtering based on selectedFeature
    let filteredStates = hierarchyData.state || [];
    let filteredDistricts = hierarchyData.district || [];

    // If a state is selected → show only that state and its districts
    if (selectedFeature?.geo_level_code === 2) {
      filteredStates = filteredStates.filter(s => s.code === selectedFeature.lgd_code);
      filteredDistricts = filteredDistricts.filter(
        d => d.parent_lgd_code === selectedFeature.lgd_code
      );
    }
    // If a district is selected → show only that district
    else if (selectedFeature?.geo_level_code === 3) {
      filteredStates = [];
      filteredDistricts = filteredDistricts.filter(
        d => d.code === selectedFeature.lgd_code
      );
    }
    // If India or nothing selected → show all
    else if (selectedFeature?.geo_level_code === 1 || !selectedFeature) {
      filteredStates = hierarchyData.state || [];
      filteredDistricts = hierarchyData.district || [];
    }

    setVisibleLabels({
      state: filterByDistance(filteredStates),
      district: filterByDistance(filteredDistricts)
    });
  }, [map, hierarchyData, zoom, selectedFeature]);

  // Update visible labels when data or zoom changes
  useEffect(() => {
    filterVisibleLabels();
  }, [filterVisibleLabels, hierarchyData, zoom]);

  const createTextIcon = (text, size = 12, color = '#1f3c88', isDistrict = false) => {
    const className = isDistrict ? 'tier-label-icon district-label-icon' : 'tier-label-icon';
    return L.divIcon({
      html: `<div>${text}</div>`,
      className: className,
      iconSize: [text.length * (size * 0.6), size + 8],
      iconAnchor: [(text.length * (size * 0.6)) / 2, (size + 8) / 2],
    });
  };

  const renderLabels = (list, zoomReq, size, color, keyPrefix, isDistrict = false) => {
    if (zoom < zoomReq || !list || list.length === 0) return null;
    const opacity = zoom >= zoomReq + 1 ? 1 : (zoom - zoomReq) * 0.5 + 0.5;

    return list.map((loc, i) =>
      loc.latitude && loc.longitude && (
        <Marker
          key={`${keyPrefix}-${loc.code}-${i}`}
          position={[parseFloat(loc.latitude), parseFloat(loc.longitude)]}
          icon={createTextIcon(loc.name, size, color, isDistrict)}
          interactive={false}
          opacity={opacity}
        />
      )
    );
  };

  return (
    <>
      {renderLabels(visibleLabels.state, 5, 12, '#1f3c88', 'state', false)}
      {renderLabels(visibleLabels.district, 7, 10, '#2a7d2e', 'district', true)}
    </>
  );
}

export default SimpleMap;